/**
 * Is `value` a CSS length a browser would accept for a margin? A plain signed
 * length, or a calc()/min()/max()/clamp() whose syntax AND types check out.
 * Anything this rejects would be invalid at computed time and zero the gap.
 */

const UNIT =
  /^(px|r?em|%|[sld]?v(h|w|min|max|i|b)|r?ch|r?ex|r?lh|r?ic|cap|cq(w|h|i|b|min|max)|pt|pc|cm|mm|q|in)$/i;
const NUMBER = /^[-+]?(\d+(\.\d+)?|\.\d+)([a-z%]+)?/i;
const FUNCTION = /^(calc|min|max|clamp)\(/i;

/** A length or a bare number — the only two types a margin's math can mix. */
type Kind = "length" | "number";

interface Cursor {
  s: string;
  i: number;
}

const rest = (c: Cursor) => c.s.slice(c.i);

const skipSpaces = (c: Cursor) => {
  while (c.s[c.i] === " ") {
    c.i += 1;
  }
};

/* Each parser returns the Kind it consumed, or null for a syntax or type
   error — CSS math rejects length + number and length * length. */

function parseValue(c: Cursor): Kind | null {
  const n = NUMBER.exec(rest(c));
  if (n) {
    c.i += n[0].length;
    if (!n[3]) {
      return "number";
    }
    return UNIT.test(n[3]) ? "length" : null;
  }
  if (c.s[c.i] === "(") {
    c.i += 1;
    skipSpaces(c);
    const kind = parseSum(c);
    skipSpaces(c);
    if (!kind || c.s[c.i] !== ")") {
      return null;
    }
    c.i += 1;
    return kind;
  }
  return parseFunction(c);
}

function parseProduct(c: Cursor): Kind | null {
  let kind = parseValue(c);
  while (kind) {
    const op = /^ *([*/]) */.exec(rest(c));
    if (!op) {
      return kind;
    }
    c.i += op[0].length;
    const right = parseValue(c);
    if (!right) {
      return null;
    }
    if (op[1] === "*") {
      if (kind === "length" && right === "length") {
        return null;
      }
      kind = kind === "length" || right === "length" ? "length" : "number";
    } else if (right !== "number") {
      return null;
    }
  }
  return null;
}

/** CSS requires whitespace on both sides of a + or - inside math. */
function parseSum(c: Cursor): Kind | null {
  const kind = parseProduct(c);
  if (!kind) {
    return null;
  }
  for (;;) {
    const op = /^ +[+-] +/.exec(rest(c));
    if (!op) {
      return kind;
    }
    c.i += op[0].length;
    if (parseProduct(c) !== kind) {
      return null;
    }
  }
}

function parseFunction(c: Cursor): Kind | null {
  const fn = FUNCTION.exec(rest(c));
  if (!fn) {
    return null;
  }
  c.i += fn[0].length;
  const name = fn[1].toLowerCase();
  const args: Kind[] = [];
  for (;;) {
    skipSpaces(c);
    const kind = parseSum(c);
    if (!kind) {
      return null;
    }
    args.push(kind);
    skipSpaces(c);
    if (c.s[c.i] !== "," || name === "calc") {
      break;
    }
    c.i += 1;
  }
  if (c.s[c.i] !== ")" || (name === "clamp" && args.length !== 3)) {
    return null;
  }
  c.i += 1;
  return args.every(kind => kind === args[0]) ? args[0] : null;
}

export function isCssLength(value: string): boolean {
  if (value === "0") {
    return true;
  }
  const plain = NUMBER.exec(value);
  if (plain?.[0] === value) {
    return Boolean(plain?.[3]) && UNIT.test(plain?.[3] ?? "");
  }
  const cursor: Cursor = {
    s: value,
    i: 0
  };
  return parseFunction(cursor) === "length" && cursor.i === value.length;
}
